import { emailAuthProvider } from './authProviders/emailAuthProvider'
import { pointClickCareAuthProvider } from './authProviders/pointClickCareAuthProvider'

export type User = {
  firstName: string
  lastName: string
  sub: string
  aud: string
  iss: string
  exp: number
  iat: number
  email: string
}

export interface AuthProviderProps {
  children: React.ReactNode
}

type Provider = {
  name: string
  code: string
}

export type SupportedProviders = 'pointClickCare' | 'useEmail'

export type Providers = {
  [K in SupportedProviders]: Provider
}

export interface OAuthProps {
  provider: keyof Providers
  loginUrl: URL
}

export const AUTH_PROVIDER_STORAGE_KEY = 'AuthProvider'

export const EMAIL_AUTH_STORAGE_KEYS = {
  accessToken: 'EmailAuthAccessToken',
  expiresAt: 'EmailAuthExpiresAt'
} as const

const isBrowser = () => typeof window !== 'undefined'

export const getStoredAuthProvider = (): SupportedProviders | null => {
  if (!isBrowser()) return null
  const stored = window.localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY)

  if (stored === 'pointClickCare' || stored === 'useEmail') {
    return stored
  }

  return null
}

export const setStoredAuthProvider = (provider: SupportedProviders | null) => {
  if (!isBrowser()) return
  if (!provider) {
    window.localStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY)
    return
  }

  window.localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, provider)
}

const providersFactory: Record<SupportedProviders, () => IAuthProvider> = {
  pointClickCare: pointClickCareAuthProvider,
  useEmail: emailAuthProvider
}

export interface IAuthProvider {
  login: () => void
  logout: () => void
  getUser: () => Promise<User | undefined>
  getToken: () => string | null
  getHeaders: () => { [key: string]: string }
}

export const getDefaultAuthProvider = (): IAuthProvider => {
  const storedProvider = getStoredAuthProvider()
  if (storedProvider) {
    return providersFactory[storedProvider]()
  }

  if (isBrowser()) {
    const emailToken = window.localStorage.getItem(EMAIL_AUTH_STORAGE_KEYS.accessToken)
    if (emailToken) {
      setStoredAuthProvider('useEmail')
      return providersFactory.useEmail()
    }
  }

  return providersFactory.pointClickCare()
}
